/* eslint-disable react-hooks/exhaustive-deps */
import { Change } from '@eagle/api-types';
import { GeofenceType } from '@eagle/core-data-types';
import { Box, Card, Divider, Stack, styled, TableBody, TableCell, TableHead, TableRow, Typography } from '@ethings/ui-toolkit';
import { Grid, Tooltip } from '@mui/material';
import { FC, useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useNavigate } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { BoldMatchedText, GeofenceListDetails, GeofenceTypesTableRow, Icon, Pagination, SearchResultsTableView, SharedChip } from '../../components';
import { T_MANY, T_ONE } from '../../constants';
import { useCustomRoutes } from '../../hooks';
import { Undefinable } from '../../types';
import { filterToQuery, getListResultDescription, includeDeletedFilters, testid, trackEvent } from '../../util';
import { FindItemsDeferredResult, ListPage, ListPageQuery } from '../list';

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledTableCell = styled(TableCell)();

export const GeofenceTypesList: FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { restClient } = useAuthenticated();
  const { geofence } = useCustomRoutes();
  const href = useHref(`/${geofence}/type`);
  const navigate = useNavigate();
  const createUrl = `/${geofence}/new-${geofence}-type`;
  const itemType = t('common:terms.geofence-type', { count: T_MANY });

  const onQueryChanged = useCallback(({ filters, pagination, search, showDeleted }: ListPageQuery): FindItemsDeferredResult<GeofenceType> => {
    const abortController = new AbortController();

    return {
      cancel: () => abortController.abort(),
      promise: restClient.geofenceType.getAllV1Paged({
        ...pagination,
        filter: { ...filterToQuery(filters), ...includeDeletedFilters(showDeleted) },
        ...(search ? { search } : {}),
        sort: !search ? JSON.stringify({ display: 'asc' }) : undefined,
      }, { signal: abortController.signal }).then((response) => {
        const itemCount = response.count;
        const resultDescription = getListResultDescription({ count: itemCount, entityKey: 'common:terms.geofence-type', filters, search, t });

        if (search) {
          trackEvent('keyword_search', 'returned_results', 'person_list', { 'results_type': 'person', 'results_count': response.count, 'search_term': search });
        }

        return {
          result: {
            results: response.entities,
            itemCount,
          },
          resultDescription,
        };
      }),
    };
  }, [restClient]);

  const renderDesktopContent = (isLoading: boolean, items: GeofenceType[]): JSX.Element => <>
    <TableHead component="div">
      <TableRow component="div">
        <StyledTableCell component="div" sx={{ width: 'auto' }}>{t('common:terms.name')}</StyledTableCell>
        <StyledTableCell component="div" sx={{ minWidth: 100 }}>{t('admin:page.geofence-types-list.shared.label')}</StyledTableCell>
      </TableRow>
    </TableHead>
    <StyledTableBody component="div" isLoading={isLoading}>
      {items.map((item, i) => <GeofenceTypesTableRow data-testid={testid`geofence-type-${item._id}`} key={i} geofenceType={item} />)}
    </StyledTableBody>
  </>;

  const renderContent = (display: string, deleted: Change | null, shared?: boolean, accountBinding?: string[]): JSX.Element =>
    <Grid container sx={{ height: '100%' }}>
      <Grid item xs={8}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="caption">{t('common:terms.name')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Tooltip placement="top" followCursor arrow disableFocusListener={!deleted} disableTouchListener={!deleted} title={<>{t('common:common.hint.deleted')}</>}>
              <Typography><BoldMatchedText text={display} /></Typography>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={4} sx={{ alignSelf: 'center' }}>
        <Grid style={{ height: '100%', textAlign: 'right' }}>
          {shared &&
            <SharedChip accountBinding={accountBinding ?? []} shared={shared} deleted={Boolean(deleted)} data-testid='shared-chip' />
          }
        </Grid>
      </Grid>
    </Grid>;

  const renderMobileContent = (displayOnCard: boolean, isLoading: boolean, items: GeofenceType[]): JSX.Element => <>
    {items.map((item, i) => {
      if (!displayOnCard) {
        return (
          <Stack key={i} width={1} style={{ filter: isLoading ? 'blur(1px)' : '' }}>
            {i === 0 && <Box my={2} mx={0}><Divider /></Box>}
            <GeofenceListDetails
              data-testid={testid`geofence-type-${item._id}`}
              data={item}
              displayOnCard={displayOnCard}
              href={href}
              renderContent={renderContent}
              shared={item.shared}
            />
          </Stack>
        );
      }

      return (
        <Card key={i} style={{ filter: isLoading ? 'blur(1px)' : '', marginTop: '8px', marginBottom: '8px', overflow: 'visible', width: '100%' }}>
          <GeofenceListDetails
            data-testid={testid`geofence-type-${item._id}`}
            data={item}
            displayOnCard={displayOnCard}
            href={href}
            shared={item.shared}
            renderContent={renderContent}
          />
        </Card>
      );
    })}
  </>;

  const renderTableContent = (
    isLoading: boolean,
    items: Undefinable<GeofenceType[]>,
    matchCount: number,
    pagination: Pagination,
    setPagination: (value: Pagination) => void,
    text: Undefinable<string>,
    error?: Error,
  ): JSX.Element => (
    <SearchResultsTableView<GeofenceType>
      data-testid='geofence-type-search-results'
      error={error}
      isLoading={isLoading}
      items={items}
      matchCount={matchCount}
      noResultsInstructions={t('common:common.hint.list.none', { entity: itemType })}
      pagination={pagination}
      renderDesktopContent={renderDesktopContent}
      renderMobileContent={renderMobileContent}
      setPagination={setPagination}
      text={text}
    />
  );

  const actions = [
    {
      href: useHref(createUrl),
      icon: <Icon icon="GeofenceIcon" />,
      label: t('common:common.action.create-entity', { entity: t('common:terms.geofence-type', { count: T_ONE }) }),
      onClick: () => navigate(createUrl),
      testId: 'create-geofence-type-button',
    },
  ];

  return (
    <ListPage<GeofenceType>
      id='geofence_type'
      actions={actions}
      data-testid='geofence-type-list'
      filterFields={[]}
      icon={<Icon icon="GeofenceIcon" />}
      navigateToEntity={() => { }}
      onQueryChanged={onQueryChanged}
      renderTableContent={renderTableContent}
      storageKey="geofence-types"
      title={t('common:terms.geofence-type', { count: T_MANY })}
    />
  );
};
